import { isAxiosError } from "axios";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  PostAuthSignUpRequestBody,
  PostAuthSignInRequestBody,
  PostAuthSignInResponse,
  PostAuthForgotPasswordRequestBody,
  PostAuthResetPasswordRequestBody,
  PostAuthSignUpRetrieveBillingRequestFlowRequestBody,
  PostAuthSignUpRetrieveBillingRequestFlowResponse,
  PostAuthSignUpCreateCustomerRequestBody,
  PostAuthSignUpCreateCustomerResponse,
  CheckEmailAvailabilityRequestQuery,
  CheckEmailAvailabilityResponse,
  CheckCompanyNameAvailabilityRequestQuery,
  CheckCompanyNameAvailabilityResponse,
  GetAuthMeResponse,
} from "../types/api/api-types";
import { axiosApi } from "./axios";
import { login } from "@/stores/authStore";
import { OpeningHours } from "@/lib/dates.util";

// Trick to get correct openingHours type for register body. Might be improved typing the openingHours in DTO
type PickUpLocationSignUpDto = Omit<
  PostAuthSignUpRequestBody["pickUpLocations"][number],
  "openingHours"
> & {
  openingHours: OpeningHours;
};
type RegisterBody = Omit<PostAuthSignUpRequestBody, "pickUpLocations"> & {
  pickUpLocations: PickUpLocationSignUpDto[];
};

const register = async (data: RegisterBody) => {
  return axiosApi.post<PostAuthSignInResponse>(`/auth/signup`, data);
};

export const useRegister = () => {
  const { t } = useTranslation("boClients");
  return useMutation({
    mutationFn: register,
    onError: (error) => {
      if (!isAxiosError(error)) return;
      if (error.response?.status === 409) {
        toast.error(t("authApi.adminEmailAlreadyUsed"));
      }
    },
  });
};

// Create GoCardless customer
const createCustomer = async (data: PostAuthSignUpCreateCustomerRequestBody) => {
  return axiosApi.post<PostAuthSignUpCreateCustomerResponse>(`/auth/create-customer`, data);
};

// Create GoCardless customer hook
export const useCreateCustomer = () => {
  return useMutation({
    mutationFn: createCustomer,
  });
};

const signUpRetrieveBillingRequestFlow = async (
  data: PostAuthSignUpRetrieveBillingRequestFlowRequestBody,
) => {
  return axiosApi.post<PostAuthSignUpRetrieveBillingRequestFlowResponse>(
    `/auth/retrieve-billing-request-flow`,
    data,
  );
};

export const useSignUpRetrieveBillingRequestFlow = () => {
  return useMutation({
    mutationFn: signUpRetrieveBillingRequestFlow,
  });
};

const signin = async (data: PostAuthSignInRequestBody) => {
  return axiosApi.post<PostAuthSignInResponse>(`/auth/signin`, data);
};

const SIGN_IN_ERROR_CODES = [
  "INVALID_CREDENTIALS",
  "COMPANY_UNPAID_INVOICE",
  "COMPANY_INACTIVE",
] as const;

export type SignInErrorCode = (typeof SIGN_IN_ERROR_CODES)[number];

export const getSignInErrorCode = (error: unknown): SignInErrorCode | undefined => {
  const message = isAxiosError(error) ? error.response?.data?.message : undefined;
  return SIGN_IN_ERROR_CODES.find((code) => code === message);
};

export const useLogin = () => {
  return useMutation({
    mutationFn: signin,
    onSuccess: ({ data }) => {
      login(data);
    },
  });
};

const forgotPassword = async (data: PostAuthForgotPasswordRequestBody) => {
  return axiosApi.post(`/auth/forgot-password`, data);
};

export const useForgotPassword = () => {
  return useMutation({
    mutationFn: forgotPassword,
  });
};

const resetPassword = async (data: PostAuthResetPasswordRequestBody) => {
  return axiosApi.post(`/auth/reset-password`, data);
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: resetPassword,
  });
};

const checkEmailAvailability = async (data: CheckEmailAvailabilityRequestQuery) => {
  return axiosApi
    .get<CheckEmailAvailabilityResponse>(`/auth/check-email-availability`, {
      params: data,
    })
    .then((res) => res.data);
};

export const useCheckEmailAvailability = () => {
  return useMutation({
    mutationFn: checkEmailAvailability,
  });
};

const checkCompanyNameAvailability = async (data: CheckCompanyNameAvailabilityRequestQuery) => {
  return axiosApi
    .get<CheckCompanyNameAvailabilityResponse>(`/auth/check-company-name-availability`, {
      params: data,
    })
    .then((res) => res.data);
};

export const useCheckCompanyNameAvailability = () => {
  return useMutation({
    mutationFn: checkCompanyNameAvailability,
  });
};

const generateLink = (email: string) => {
  return axiosApi.post("/auth/generate-link", { email });
};

export const useGenerateLink = () => {
  const { t } = useTranslation("boClients");
  return useMutation({
    mutationFn: generateLink,
    onSuccess: () => {
      toast.success(t("authApi.generateLinkSuccess"));
    },
    onError: () => {
      toast.error(t("authApi.generateLinkError"));
    },
  });
};

const verifyToken = (token: string) => {
  return axiosApi.get("/auth/verify-token", { params: { token } });
};

export const useVerifyToken = (token: string) => {
  return useQuery({
    queryKey: ["verify-token"],
    queryFn: () => verifyToken(token),
    retry: false,
  });
};

export const me = async () => {
  return axiosApi.get<GetAuthMeResponse>(`/auth/me`).then((res) => res.data);
};
